/**
 * ProvisionDialog — DE Studio modal that provisions N tenant clones from a
 * single source assistant. Opens via the "Provision" affordance on Workforce
 * cards or the AssistantWorkspace header.
 *
 * Stages:
 *   1. FORM     — enter count (1..100), submit
 *   2. RUNNING  — streaming NDJSON log + progress bar
 *   3. DONE     — summary, "Open first portal" + "Close" actions
 *
 * On close after success, fires `onCompleted(results)` so the parent can
 * refresh its assistant list.
 */
import React, {
    useEffect, useRef, useState, useCallback
} from 'react';
import {
    Rocket, Loader2, X, Check, AlertCircle, ExternalLink
} from 'lucide-react';

const STATUS = {
    FORM:    'form',
    RUNNING: 'running',
    DONE:    'done',
};

const ICON_BY_TYPE = {
    log:       null,
    progressOk:    Check,
    progressError: AlertCircle,
    done:      Check,
};

function fmtClock() {
    const d = new Date();
    const pad = (n) => String(n).padStart(2, '0');
    return `${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
}

export default function ProvisionDialog({
    open,
    assistant, // { assistant_id, title }
    organizationId,
    subOrganizationId,
    onClose,
    onCompleted, // (results: array) => void  — fired when DONE + dialog closing
}) {
    const [stage, setStage] = useState(STATUS.FORM);
    const [count, setCount] = useState(1);
    const [events, setEvents] = useState([]); // {kind, ts, label, sub?}
    const [progress, setProgress] = useState({
        current: 0,
        total: 0,
        ok: 0,
        fail: 0
    });
    const [results, setResults] = useState([]);
    const [error, setError] = useState(null);
    const abortRef = useRef(null);
    const logBoxRef = useRef(null);

    // Reset state when dialog (re)opens.
    useEffect(() => {
        if (!open) return;
        setStage(STATUS.FORM);
        setCount(1);
        setEvents([]);
        setProgress({
            current: 0,
            total: 0,
            ok: 0,
            fail: 0
        });
        setResults([]);
        setError(null);
    }, [open]);

    // Auto-scroll the log on new events.
    useEffect(() => {
        if (logBoxRef.current) {
            logBoxRef.current.scrollTop = logBoxRef.current.scrollHeight;
        }
    }, [events]);

    const pushEvent = useCallback((kind, label, sub) => {
        setEvents((prev) => [...prev, {
            kind,
            label,
            sub,
            ts: fmtClock()
        }]);
    }, []);

    async function handleStart() {
        // Lazy import so the dialog file stays standalone-import-safe.
        const {
            provisionBulkStream
        } = await import('./provisionAssistant');

        setStage(STATUS.RUNNING);
        setProgress({
            current: 0,
            total: count,
            ok: 0,
            fail: 0
        });
        const ctrl = new AbortController();
        abortRef.current = ctrl;
        const collected = [];

        try {
            await provisionBulkStream({
                assistantId: assistant.assistant_id,
                organizationId,
                subOrganizationId,
                count,
                signal: ctrl.signal,
                onEvent: (evt) => {
                    if (evt.type === 'log') {
                        pushEvent('log', evt.message);
                    } else if (evt.type === 'progress') {
                        if (evt.status === 'ok') {
                            collected.push({
                                assistant_id: evt.assistant_id
                            });
                            pushEvent(
                                'progressOk',
                                `[${evt.index}/${evt.total}] Provisioned`,
                                evt.assistant_id,
                            );
                            setProgress((p) => ({
                                ...p,
                                current: evt.index,
                                ok: p.ok + 1,
                            }));
                        } else {
                            pushEvent(
                                'progressError',
                                `[${evt.index}/${evt.total}] Failed`,
                                evt.message || 'Unknown error',
                            );
                            setProgress((p) => ({
                                ...p,
                                current: evt.index,
                                fail: p.fail + 1,
                            }));
                        }
                    } else if (evt.type === 'done') {
                        pushEvent('done', evt.message);
                        setResults(evt.data || collected);
                        setStage(STATUS.DONE);
                    }
                },
            });
            // If server didn't emit "done" but stream ended.
            setStage((s) => (s === STATUS.RUNNING ? STATUS.DONE : s));
            setResults((r) => (r.length ? r : collected));
        } catch (err) {
            if (err.name === 'AbortError') {
                pushEvent('progressError', 'Cancelled by user');
            } else {
                setError(err?.message || 'Provision request failed');
                pushEvent('progressError', 'Request failed', err?.message);
            }
            setStage(STATUS.DONE);
        } finally {
            abortRef.current = null;
        }
    }

    function handleCancelInFlight() {
        if (abortRef.current) abortRef.current.abort();
    }

    function handleClose() {
        // If running, abort first.
        if (abortRef.current) abortRef.current.abort();
        // Notify parent if anything was provisioned so the list refreshes.
        if (results.length > 0 && onCompleted) onCompleted(results);
        onClose?.();
    }

    function openFirstPortal() {
        if (results.length === 0) return;
        const id = results[0].assistant_id;
        if (!id) return;
        window.open(`/provisioned-config?instance=${encodeURIComponent(id)}`, '_blank', 'noopener,noreferrer');
    }

    if (!open) return null;

    const pct = progress.total > 0
        ? Math.round((progress.current / progress.total) * 100)
        : 0;

    return (
        <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="provision-dlg-title"
            onMouseDown={(e) => { if (e.target === e.currentTarget) handleClose(); }}
            style={{
                position: 'fixed',
                inset: 0,
                zIndex: 1000,
                background: 'rgba(20, 20, 30, 0.55)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: 16,
            }}
        >
            <div style={{
                width: '100%',
                maxWidth: 560,
                background: 'var(--ds-surface, #fff)',
                color: 'var(--ds-text, #1a1a1a)',
                borderRadius: 'var(--ds-radius, 10px)',
                boxShadow: '0 24px 60px rgba(0, 0, 0, 0.25)',
                display: 'flex',
                flexDirection: 'column',
                maxHeight: '85vh',
                overflow: 'hidden',
            }}>
                {/* Header */}
                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 10,
                    padding: '14px 16px',
                    borderBottom: '1px solid var(--ds-divider, #ececec)',
                }}>
                    <div style={{
                        width: 28,
                        height: 28,
                        borderRadius: 8,
                        background: 'var(--ds-primary-soft, rgba(99, 102, 241, 0.1))',
                        color: 'var(--ds-primary, #6366f1)',
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                    }}>
                        <Rocket size={15} />
                    </div>
                    <div style={{
                        flex: 1,
                        minWidth: 0
                    }}>
                        <div id="provision-dlg-title" style={{
                            fontWeight: 600,
                            fontSize: 14,
                            lineHeight: 1.2
                        }}>
                            Provision tenant{count > 1 ? 's' : ''}
                        </div>
                        <div style={{
                            fontSize: 12,
                            color: 'var(--ds-text-muted, #6b7280)',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                        }} title={assistant.assistant_id}>
                            from {assistant.title || assistant.assistant_id}
                        </div>
                    </div>
                    <button
                        type="button"
                        onClick={handleClose}
                        aria-label="Close"
                        style={{
                            border: 'none',
                            background: 'transparent',
                            cursor: 'pointer',
                            color: 'var(--ds-text-muted, #6b7280)',
                            padding: 4,
                            borderRadius: 6,
                        }}
                    >
                        <X size={16} />
                    </button>
                </div>

                {/* Body */}
                <div style={{
                    padding: 16,
                    overflow: 'auto'
                }}>
                    {stage === STATUS.FORM && (
                        <Form
                            count={count}
                            setCount={setCount}
                            onStart={handleStart}
                            onClose={handleClose}
                        />
                    )}
                    {(stage === STATUS.RUNNING || stage === STATUS.DONE) && (
                        <ProgressLog
                            stage={stage}
                            progress={progress}
                            pct={pct}
                            events={events}
                            results={results}
                            error={error}
                            onCancel={handleCancelInFlight}
                            onClose={handleClose}
                            onOpenFirst={openFirstPortal}
                            logBoxRef={logBoxRef}
                        />
                    )}
                </div>
            </div>
        </div>
    );
}

function Form({
    count, setCount, onStart, onClose
}) {
    return (
        <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 14
        }}>
            <p style={{
                margin: 0,
                fontSize: 13,
                color: 'var(--ds-text-muted, #6b7280)',
                lineHeight: 1.5,
            }}>
                Each provision deep-clones the assistant (opcodes, instructions, channels)
                into a new tenant identity in the same sub-org. You'll get one configuration
                URL per clone.
            </p>
            <label style={{
                display: 'flex',
                flexDirection: 'column',
                gap: 6
            }}>
                <span style={{
                    fontSize: 12,
                    fontWeight: 600,
                    color: 'var(--ds-text, #1a1a1a)'
                }}>
                    How many tenant instances?
                </span>
                <input
                    type="number"
                    min={1}
                    max={100}
                    value={count}
                    onChange={(e) => {
                        const n = parseInt(e.target.value, 10);
                        if (Number.isNaN(n)) { setCount(1); return; }
                        setCount(Math.max(1, Math.min(100, n)));
                    }}
                    autoFocus
                    style={{
                        width: '100%',
                        padding: '8px 10px',
                        border: '1px solid var(--ds-divider, #d4d4d8)',
                        borderRadius: 6,
                        fontSize: 14,
                        background: 'var(--ds-surface, #fff)',
                        color: 'inherit',
                    }}
                />
                <span style={{
                    fontSize: 11,
                    color: 'var(--ds-text-muted, #6b7280)'
                }}>
                    Range 1–100. Each clone takes about 1–3 seconds.
                </span>
            </label>
            <div style={{
                display: 'flex',
                justifyContent: 'flex-end',
                gap: 8,
                paddingTop: 4
            }}>
                <button
                    type="button"
                    onClick={onClose}
                    style={btn({
                        variant: 'ghost'
                    })}
                >
                    Cancel
                </button>
                <button
                    type="button"
                    onClick={onStart}
                    style={btn({
                        variant: 'primary'
                    })}
                >
                    <Rocket size={13} /> Provision {count}
                </button>
            </div>
        </div>
    );
}

function ProgressLog({
    stage, progress, pct, events, results, error,
    onCancel, onClose, onOpenFirst, logBoxRef,
}) {
    const running = stage === STATUS.RUNNING;
    const allOk = stage === STATUS.DONE && progress.fail === 0 && !error;
    return (
        <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 12
        }}>
            {/* Progress header */}
            <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 8
            }}>
                <div style={{
                    fontSize: 13,
                    fontWeight: 600
                }}>
                    {running
                        ? <><Loader2 size={13} className="animate-spin" style={{
                            verticalAlign: -2
                        }} /> Provisioning…</>
                        : (allOk ? '✓ Completed' : (error ? 'Failed' : 'Completed with errors'))}
                </div>
                <div style={{
                    fontSize: 12,
                    color: 'var(--ds-text-muted, #6b7280)'
                }}>
                    {progress.current}/{progress.total}
                    {progress.ok > 0 && <> · <span style={{
                        color: 'var(--ds-success, #10b981)'
                    }}>{progress.ok} ok</span></>}
                    {progress.fail > 0 && <> · <span style={{
                        color: 'var(--ds-danger, #ef4444)'
                    }}>{progress.fail} fail</span></>}
                </div>
            </div>
            {/* Progress bar */}
            <div style={{
                height: 6,
                width: '100%',
                borderRadius: 999,
                background: 'var(--ds-divider, #ececec)',
                overflow: 'hidden',
            }}>
                <div style={{
                    height: '100%',
                    width: `${pct}%`,
                    background: error
                        ? 'var(--ds-danger, #ef4444)'
                        : (allOk ? 'var(--ds-success, #10b981)' : 'var(--ds-primary, #6366f1)'),
                    transition: 'width 220ms ease',
                }} />
            </div>
            {/* Log box */}
            <div
                ref={logBoxRef}
                style={{
                    background: 'var(--ds-bg-elevated, #0f172a)',
                    color: '#e5e7eb',
                    fontFamily: 'var(--ds-font-mono, monospace)',
                    fontSize: 12,
                    lineHeight: 1.5,
                    padding: 12,
                    borderRadius: 6,
                    maxHeight: 280,
                    minHeight: 160,
                    overflow: 'auto',
                    whiteSpace: 'pre-wrap',
                }}
            >
                {events.length === 0 && (
                    <span style={{
                        color: '#9ca3af'
                    }}>Waiting for first event…</span>
                )}
                {events.map((ev, i) => (
                    <LogLine key={i} ev={ev} />
                ))}
            </div>
            {/* Footer actions */}
            <div style={{
                display: 'flex',
                justifyContent: 'flex-end',
                gap: 8
            }}>
                {running ? (
                    <button type="button" onClick={onCancel} style={btn({
                        variant: 'ghost'
                    })}>
                        Cancel
                    </button>
                ) : (
                    <>
                        {results.length > 0 && (
                            <button type="button" onClick={onOpenFirst} style={btn({
                                variant: 'secondary'
                            })}>
                                <ExternalLink size={13} /> Open first tenant
                            </button>
                        )}
                        <button type="button" onClick={onClose} style={btn({
                            variant: 'primary'
                        })}>
                            Done
                        </button>
                    </>
                )}
            </div>
        </div>
    );
}

function LogLine({
    ev
}) {
    const Icon = ICON_BY_TYPE[ev.kind];
    const tone = ev.kind === 'progressError' ? '#fca5a5'
        : ev.kind === 'progressOk' ? '#86efac'
            : ev.kind === 'done' ? '#a5b4fc'
                : '#cbd5e1';
    return (
        <div style={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: 8,
            padding: '2px 0'
        }}>
            <span style={{
                color: '#64748b',
                flexShrink: 0
            }}>{ev.ts}</span>
            <span style={{
                color: tone,
                flexShrink: 0,
                width: 14,
                display: 'inline-flex',
                alignItems: 'center'
            }}>
                {Icon ? <Icon size={11} /> : null}
            </span>
            <span style={{
                color: tone,
                minWidth: 0,
                wordBreak: 'break-all'
            }}>
                {ev.label}
                {ev.sub && <span style={{
                    color: '#94a3b8'
                }}> — {ev.sub}</span>}
            </span>
        </div>
    );
}

function btn({
    variant
}) {
    const base = {
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        padding: '7px 12px',
        borderRadius: 6,
        fontSize: 13,
        fontWeight: 600,
        cursor: 'pointer',
        border: '1px solid transparent',
    };
    if (variant === 'primary') {
        return {
            ...base,
            background: 'var(--ds-primary, #6366f1)',
            color: '#fff',
            borderColor: 'var(--ds-primary, #6366f1)',
        };
    }
    if (variant === 'secondary') {
        return {
            ...base,
            background: 'var(--ds-primary-soft, rgba(99, 102, 241, 0.1))',
            color: 'var(--ds-primary, #6366f1)',
            borderColor: 'transparent',
        };
    }
    return {
        ...base,
        background: 'transparent',
        color: 'var(--ds-text-muted, #6b7280)',
        borderColor: 'var(--ds-divider, #d4d4d8)',
    };
}
